import React, { useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';

function convertEventName(propName: string): string {
  return propName
    .replace(/^on/, '')
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');
}

export function createWebComponent<T extends HTMLElement>(
  tagName: string
) {
  return forwardRef<T, React.HTMLAttributes<T> & Record<string, any>>(
    (props, ref) => {
      const elementRef = useRef<T>(null);
      const eventHandlersRef = useRef<Map<string, (e: Event) => void>>(new Map());

      useImperativeHandle(ref, () => elementRef.current as T);

      const { eventProps, elementProps } = useMemo(() => {
        const events: Record<string, (e: Event) => void> = {};
        const attrs: Record<string, any> = {};

        Object.keys(props).forEach((key) => {
          if (key.startsWith('on') && typeof props[key] === 'function') {
            events[key] = props[key];
          } else if (key !== 'children' && key !== 'ref') {
            attrs[key] = props[key];
          }
        });

        return { eventProps: events, elementProps: attrs };
      }, [props]);

      useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        Object.entries(eventProps).forEach(([propName, handler]) => {
          const eventName = convertEventName(propName);
          const prevHandler = eventHandlersRef.current.get(eventName);
          
          if (prevHandler) {
            element.removeEventListener(eventName, prevHandler);
          }
          
          eventHandlersRef.current.set(eventName, handler);
          element.addEventListener(eventName, handler);
        });

        return () => {
          eventHandlersRef.current.forEach((handler, eventName) => {
            element.removeEventListener(eventName, handler);
          });
          eventHandlersRef.current.clear();
        };
      }, [eventProps]);

      useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        Object.entries(elementProps).forEach(([key, value]) => {
          if (typeof value === 'boolean') {
            if (value) {
              element.setAttribute(key, '');
            } else {
              element.removeAttribute(key);
            }
          } else if (value !== undefined && value !== null) {
            (element as any)[key] = value;
          }
        });
      }, [elementProps]);

      return React.createElement(tagName, { ref: elementRef }, props.children);
    }
  );
}
